import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ReviewerShell from '@/components/reviewer/ReviewerShell';
import { getReviewPanel, type CellStatus, type PanelModel, type PanelScript } from '@/services/mlReviewService';
import { cn } from '@/lib/utils';

const STATUS_CLASS: Record<CellStatus, string> = {
  pending: 'bg-white/10 text-white/50',
  in_progress: 'bg-amber-500/20 text-amber-200',
  gold: 'bg-emerald-500/20 text-emerald-200',
  missing: 'bg-red-500/20 text-red-200',
};

export default function ReviewerHome() {
  const [scripts, setScripts] = useState<PanelScript[]>([]);
  const [models, setModels] = useState<PanelModel[]>([]);
  const [cells, setCells] = useState<{ script_id: string; model: string; status: CellStatus }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<'model-major' | 'script-major'>('model-major');

  useEffect(() => {
    void getReviewPanel()
      .then((data) => {
        setScripts(data.panel.scripts || []);
        setModels(data.panel.models || []);
        setCells(data.cells || []);
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load panel'))
      .finally(() => setLoading(false));
  }, []);

  const statusOf = (scriptId: string, model: string): CellStatus =>
    cells.find((c) => c.script_id === scriptId && c.model === model)?.status || 'pending';

  const nextLink = useMemo(() => {
    const walk =
      order === 'model-major'
        ? models.flatMap((m) => scripts.map((s) => ({ s, m })))
        : scripts.flatMap((s) => models.map((m) => ({ s, m })));
    const next = walk.find(({ s, m }) => {
      const st = statusOf(s.script_id, m.slug);
      return st === 'pending' || st === 'in_progress';
    });
    return next ? `/reviewer/${next.s.script_id}/${next.m.slug}` : null;
  }, [order, models, scripts, cells]);

  const gold = cells.filter((c) => c.status === 'gold').length;

  return (
    <ReviewerShell subtitle="40 scripts × 8 models">
      <div className="space-y-6">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="ni-label mb-2">Human gold panel</p>
            <h1 className="text-2xl font-sans font-bold">Script label review</h1>
            <p className="text-sm text-white/50 mt-2">
              {gold} / {cells.length || 320} gold. Work W1 before later waves.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={order}
              onChange={(e) => setOrder(e.target.value as 'model-major' | 'script-major')}
              className="rounded-md border border-[#2a2a2a] bg-[#0d0d0d] px-3 py-2 text-sm"
            >
              <option value="model-major">Model-major (W1 across scripts)</option>
              <option value="script-major">Script-major (all models per title)</option>
            </select>
            {nextLink && (
              <Link
                to={nextLink}
                className="rounded-sm bg-white/10 px-3 py-2 text-sm font-sans hover:bg-white/15"
              >
                Continue
              </Link>
            )}
          </div>
        </header>

        {error && <p className="text-red-300 text-sm">{error}</p>}
        {loading && <p className="text-white/40 text-sm">Loading panel…</p>}

        {!loading && (
          <div className="overflow-auto ni-scroll border border-[#2a2a2a]">
            <table className="min-w-[960px] w-full text-left text-xs">
              <thead className="bg-[#111] sticky top-0">
                <tr>
                  <th className="p-2 font-mono text-white/50">Script</th>
                  {models.map((m) => (
                    <th key={m.slug} className="p-2 font-mono text-white/50" title={m.blurb}>
                      {m.wave}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {scripts.map((s) => (
                  <tr key={s.script_id} className="border-t border-[#222]">
                    <td className="p-2 font-sans text-white/80 whitespace-nowrap">{s.title}</td>
                    {models.map((m) => {
                      const st = statusOf(s.script_id, m.slug);
                      return (
                        <td key={m.slug} className="p-1">
                          <Link
                            to={`/reviewer/${s.script_id}/${m.slug}`}
                            className={cn(
                              'block rounded-sm px-2 py-1 text-center font-mono uppercase',
                              STATUS_CLASS[st],
                            )}
                          >
                            {st === 'in_progress' ? 'wip' : st}
                          </Link>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ReviewerShell>
  );
}
